/**
 * Constants and label mappings for product detail components
 */

import type {
  CapabilityStatus,
  PricingPosition,
  RevenueModel,
  ShareTrend,
  FeatureStatus,
} from '../../../types';

export const revenueModelLabels: Record<RevenueModel, string> = {
  saas: 'SaaS',
  transaction: 'Transaction-Based',
  license: 'License',
  ad_supported: 'Ad-Supported',
  freemium: 'Freemium',
  usage_based: 'Usage-Based',
  distribution: 'Distribution',
  services: 'Services',
};

export const pricingPositionLabels: Record<PricingPosition, string> = {
  premium: 'Premium',
  mid_market: 'Mid-Market',
  value: 'Value',
  free: 'Free (Monetized Elsewhere)',
  unknown: 'Unknown',
};

export const shareTrendLabels: Record<ShareTrend, { label: string; color: string }> = {
  growing: { label: 'Growing', color: 'text-green-600' },
  stable: { label: 'Stable', color: 'text-gray-600' },
  declining: { label: 'Declining', color: 'text-red-600' },
  unknown: { label: 'Unknown', color: 'text-gray-400' },
};

export const capabilityStatusColors: Record<CapabilityStatus, { bg: string; text: string }> = {
  mature: { bg: 'bg-green-100', text: 'text-green-800' },
  in_development: { bg: 'bg-blue-100', text: 'text-blue-800' },
  acquired_not_integrated: { bg: 'bg-purple-100', text: 'text-purple-800' },
  beta: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  deprecated: { bg: 'bg-gray-100', text: 'text-gray-600' },
};

export const capabilityStatusLabels: Record<CapabilityStatus, string> = {
  mature: 'Mature',
  in_development: 'In Dev',
  acquired_not_integrated: 'Acquired',
  beta: 'Beta',
  deprecated: 'Deprecated',
};

export const featureStatusStyles: Record<
  FeatureStatus,
  { bg: string; text: string; label: string }
> = {
  better: { bg: 'bg-green-100', text: 'text-green-800', label: 'Better' },
  parity: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Parity' },
  worse: { bg: 'bg-red-100', text: 'text-red-800', label: 'Worse' },
  missing: { bg: 'bg-gray-50', text: 'text-gray-400', label: 'Missing' },
};

export const platformLabels: Record<string, string> = {
  g2: 'G2',
  capterra: 'Capterra',
  klas: 'KLAS Research',
  glassdoor: 'Glassdoor',
};

export const relationshipColors = {
  direct: 'border-red-200 bg-red-50',
  indirect: 'border-yellow-200 bg-yellow-50',
  adjacent: 'border-blue-200 bg-blue-50',
} as const;

export const relationshipBorderColors = {
  direct: 'border-red-300 hover:border-red-400',
  indirect: 'border-yellow-300 hover:border-yellow-400',
  adjacent: 'border-blue-300 hover:border-blue-400',
} as const;

export const relationshipBadgeColors = {
  direct: 'bg-red-100 text-red-700',
  indirect: 'bg-yellow-100 text-yellow-700',
  adjacent: 'bg-blue-100 text-blue-700',
} as const;
